import React, { useState, useContext } from 'react';
import styled from '@emotion/styled';
import * as Yup from 'yup';
import ModalContainer from '../ModalContainer';
import { ModalContext } from '../../../context/ModalContext';
import { FormComponent } from '../../Form/FormComponent';
import ValidatingFormSubmitButton from '../../Button/FormSubmitButton';
import { CustomRadio, CustomRadioGroup } from '../../Form/CustomRadioField';
import { OccupationList } from '../../../pages/Dashboard/Settings/ProfileSettings/ProfileSettingsBottomSection/BasicInformation/BasicInformationData';
import { useLocumSearch } from '../../../context/LocumSearchContext';

// Array of all Nigerian states + FCT (shown as Abuja)
const nigeriaStates = [
  'Abia',
  'Abuja',
  'Adamawa',
  'Akwa Ibom',
  'Anambra',
  'Bauchi',
  'Bayelsa',
  'Benue',
  'Borno',
  'Cross River',
  'Delta',
  'Ebonyi',
  'Edo',
  'Ekiti',
  'Enugu',
  'Gombe',
  'Imo',
  'Jigawa',
  'Kaduna',
  'Kano',
  'Katsina',
  'Kebbi',
  'Kogi',
  'Kwara',
  'Lagos',
  'Nasarawa',
  'Niger',
  'Ogun',
  'Ondo',
  'Osun',
  'Oyo',
  'Plateau',
  'Rivers',
  'Sokoto',
  'Taraba',
  'Yobe',
  'Zamfara',
].sort();

// Initial form values & validation
const initialValues = {
  location: 'Any',
  occupation: 'Any',
};

const validationSchema = Yup.object().shape({
  location: Yup.string().required('Location is required'),
  occupation: Yup.string().required('Occupation is required'),
});

const LocumFilterModal: React.FC = () => {
  const [showAllStates, setShowAllStates] = useState(false);
  const modalContext = useContext(ModalContext);

  const { setLocation, setOccupation, setPage } = useLocumSearch();

  const onSubmit = (values: any) => {
    // reset to first page whenever filters change
    setPage('1');
    // push real filters into context
    setLocation(values.location === 'ANY' ? '' : values.location);
    setOccupation(values.occupation === 'ANY' ? '' : values.occupation);
    modalContext?.displayModal(false);
  };

  // Display first five if not expanded; otherwise, display all
  const statesToDisplay = showAllStates
    ? nigeriaStates
    : nigeriaStates.slice(0, 5);

  return (
    <ModalContainer width="30rem" padding="2rem" marginTop="3rem">
      <Header>Locum Filter</Header>
      <Container>
        <FormComponent
          initialValues={initialValues}
          schema={validationSchema}
          onSubmit={onSubmit}
        >
          <Section>
            <h4>Location</h4>
            <CustomRadioGroup name="location">
              <CustomRadio name="location" value="ANY">
                Any
              </CustomRadio>
              {statesToDisplay.map((state) => (
                <CustomRadio key={state} name="location" value={state}>
                  {state}
                </CustomRadio>
              ))}
              <ToggleText onClick={() => setShowAllStates((prev) => !prev)}>
                {showAllStates ? 'Less' : 'More (show 37 locations)'}
              </ToggleText>
            </CustomRadioGroup>
          </Section>

          <Section>
            <h4>Occupation</h4>
            <CustomRadioGroup name="occupation">
              <CustomRadio name="occupation" value="ANY">
                Any
              </CustomRadio>
              {OccupationList.filter((occ) => occ !== 'Medical Recruiter').map(
                (occ) => (
                  <CustomRadio key={occ} name="occupation" value={occ}>
                    {occ}
                  </CustomRadio>
                )
              )}
            </CustomRadioGroup>
          </Section>

          <ValidatingFormSubmitButton className="fillParent">
            Apply
          </ValidatingFormSubmitButton>
        </FormComponent>
      </Container>
    </ModalContainer>
  );
};

export default LocumFilterModal;

const Container = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  align-items: flex-start;
  max-width: 600px;
`;

const Section = styled.div`
  & > h4 {
    font-weight: 400;
    font-size: 24px;
    margin-bottom: 2rem;
  }
`;

const Header = styled.div`
  font-weight: 400;
  font-size: 32px;
  margin-bottom: 1.5rem;
`;

const ToggleText = styled.span`
  display: block;
  margin-top: 1rem;
  font-size: 20px;
  cursor: pointer;
  text-decoration: underline;
`;
